import { Download } from "lucide-react";

import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { type ViewedAnalyticsOutput } from "../../../../../hooks/audit/useAnalyticsOutputFiles";
import { type AnalyticsRunOutputSummary } from "../../../../../models/audit/analyticsWorkflow";
import FileViewer from "../../../../extraction/viewExtraction/FileViewer";

interface AnalyticsOutputViewerDialogProps {
  open: boolean;
  /**
   * The output the viewer draws. Kept while the dialog animates closed, and
   * null only once it has.
   */
  viewed: ViewedAnalyticsOutput | null;
  onClose: () => void;
  /** Called once the dialog has finished closing. */
  onClosed: () => void;
  /** Mints a fresh URL and hands the file over, as the row's own button does. */
  onDownload: (output: AnalyticsRunOutputSummary) => void;
  /** File ids whose URL is being minted. */
  pendingFileIds: ReadonlySet<string>;
}

/**
 * A published output open in the viewer every other spreadsheet in Midship
 * opens in: the CSV grid for a `.csv`, the Excel viewer for a workbook.
 *
 * The sheet's viewer, because the sheet has no viewer pane. The fold-out opens
 * the same file in its own pane instead (`AnalyticsOutputViewerPane`).
 *
 * **Download stays one press away.** A viewer that cannot draw a file says so
 * in its own body, and the way out of that is the file itself; it is minted
 * afresh rather than reusing the URL the viewer was opened with, because that
 * one was granted for the view.
 */
export function AnalyticsOutputViewerDialog({
  open,
  viewed,
  onClose,
  onClosed,
  onDownload,
  pendingFileIds,
}: AnalyticsOutputViewerDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="flex h-[85vh] max-w-[90vw] flex-col gap-0 p-0"
        onCloseAutoFocus={onClosed}
      >
        {viewed && (
          <>
            <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b border-stone-200 py-3 pl-4 pr-12">
              <div className="min-w-0 flex-1">
                <DialogTitle className="truncate text-base">
                  {viewed.output.fileName}
                </DialogTitle>
                <DialogDescription
                  className={
                    viewed.output.description === null
                      ? "sr-only"
                      : "truncate text-xs"
                  }
                >
                  {viewed.output.description ??
                    "An output file published by this analytics run."}
                </DialogDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                aria-label={`Download ${viewed.output.fileName}`}
                disabled={pendingFileIds.has(viewed.output.fileId)}
                onClick={() => onDownload(viewed.output)}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-hidden">
              <FileViewer
                // Keyed by the grant, so a second file opened over the first
                // starts its viewer from nothing rather than from the last
                // file's state.
                key={viewed.url}
                signedUrl={viewed.url}
                fileType={viewed.fileType}
                fileName={viewed.output.fileName}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
